import React from 'react';
import { Menu, MenuButton, MenuList, MenuItem, IconButton, Icon } from '@chakra-ui/react';
import { MenuPortal, useMenuStrategy } from './portalTarget';
import { FiChevronDown, FiCornerUpLeft, FiCopy, FiInfo, FiStar, FiDownload } from 'react-icons/fi';

/**
 * The per-message menu behind WhatsApp's hover chevron.
 *
 * ★ ONLY WHAT WORKS IS LISTED. WhatsApp offers Reply · React · Forward · Star
 * · Pin · Copy · Delete · Info. Reactions, forwarding, deleting and editing
 * have no endpoint behind them here, so they are not drawn — a menu item that
 * fails is worse than no item. Star is per-device and says so.
 *
 * Controlled (`isOpen`/`onClose`) so a long-press on touch can open the same
 * menu the chevron opens with a mouse.
 */
export default function MessageMenu({
	outbound,
	canReply,
	hasText,
	hasMedia,
	starred,
	isOpen,
	onOpen,
	onClose,
	onReply,
	onCopy,
	onInfo,
	onStar,
	onSave,
}) {
	const strategy = useMenuStrategy();
	return (
		<Menu
			isOpen={isOpen}
			onOpen={onOpen}
			onClose={onClose}
			placement={outbound ? 'bottom-end' : 'bottom-start'}
			strategy={strategy}
			isLazy
		>
			<MenuButton
				as={IconButton}
				aria-label="Message options"
				icon={<Icon as={FiChevronDown} boxSize="18px" />}
				size="xs"
				variant="ghost"
				borderRadius="full"
				position="absolute"
				top="1"
				right="1"
				color="wa.inkMuted"
				bg={outbound ? 'wa.outgoing' : 'wa.incoming'}
				opacity={isOpen ? 1 : 0}
				_groupHover={{ opacity: 1 }}
				_focus={{ opacity: 1, boxShadow: 'none' }}
				_hover={{ color: 'wa.ink' }}
				transition="opacity 120ms ease"
				zIndex="1"
				onClick={(e) => e.stopPropagation()}
			/>
			<MenuPortal>
				<MenuList zIndex="popover" bg="wa.menu" borderColor="wa.divider" color="wa.ink" minW="180px" boxShadow="lg" fontSize="sm">
					{canReply && (
						<MenuItem icon={<Icon as={FiCornerUpLeft} />} onClick={onReply} _hover={{ bg: 'wa.hover' }} _focus={{ bg: 'wa.hover' }}>
							Reply
						</MenuItem>
					)}
					{hasText && (
						<MenuItem icon={<Icon as={FiCopy} />} onClick={onCopy} _hover={{ bg: 'wa.hover' }} _focus={{ bg: 'wa.hover' }}>
							Copy
						</MenuItem>
					)}
					{hasMedia && (
						<MenuItem icon={<Icon as={FiDownload} />} onClick={onSave} _hover={{ bg: 'wa.hover' }} _focus={{ bg: 'wa.hover' }}>
							Save
						</MenuItem>
					)}
					<MenuItem
						icon={<Icon as={FiStar} color={starred ? 'wa.accent' : undefined} />}
						onClick={onStar}
						title="Kept on this device"
						_hover={{ bg: 'wa.hover' }}
						_focus={{ bg: 'wa.hover' }}
					>
						{starred ? 'Unstar' : 'Star'}
					</MenuItem>
					{outbound && (
						<MenuItem icon={<Icon as={FiInfo} />} onClick={onInfo} _hover={{ bg: 'wa.hover' }} _focus={{ bg: 'wa.hover' }}>
							Message info
						</MenuItem>
					)}
				</MenuList>
			</MenuPortal>
		</Menu>
	);
}
